import { useCallback, useEffect, useRef, useState } from "react";

import { useConversationMessages } from "./conversation.hooks";
import { ChatMessage } from "./useBackofficeChat";
import { extractBrowserPlansFromContent } from "../utils/browser-plans";

interface UseConversationResumeProps {
  agencyId?: string;
  loadConversation: (id: string, messages: ChatMessage[]) => void;
  resetPlanExecutor: () => void;
  resetChat: () => void;
}

interface UseConversationResumeReturn {
  isLoadingResume: boolean;
  reset: () => void;
}

export const useConversationResume = ({
  agencyId,
  loadConversation,
  resetPlanExecutor,
  resetChat,
}: UseConversationResumeProps): UseConversationResumeReturn => {
  const [resumeConversationID, setResumeConversationID] = useState<string | null>(null);
  const hasLoadedRef = useRef<string | null>(null);

  const { data: resumeData, isLoading: isLoadingResume } = useConversationMessages(
    resumeConversationID,
    agencyId
  );

  // Load conversation history when API data arrives
  useEffect(() => {
    if (!resumeConversationID || !resumeData || hasLoadedRef.current === resumeConversationID) {
      return;
    }
    const rawMessages = (resumeData as any)?.data?.messages ?? (resumeData as any)?.messages ?? [];
    const mapped: ChatMessage[] = rawMessages.map((m: any) => {
      const { cleanedContent, plans } = extractBrowserPlansFromContent(m.content || "");
      return {
        id: m.conversationMessageID || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        role: m.role,
        content: cleanedContent,
        policySources: m.metadata?.policySources ?? undefined,
        browserPlans: plans.length > 0 ? plans : undefined,
      };
    });
    loadConversation(resumeConversationID, mapped);
    resetPlanExecutor();
    hasLoadedRef.current = resumeConversationID;
    setResumeConversationID(null);
  }, [resumeConversationID, resumeData, loadConversation, resetPlanExecutor]);

  // Listen for sidebar conversation selection and new-chat reset
  useEffect(() => {
    const handleResumeChat = (e: Event) => {
      const { detail } = e as CustomEvent;
      if (detail?.conversationID) {
        hasLoadedRef.current = null;
        setResumeConversationID(detail.conversationID);
      }
    };
    const handleNewChat = () => {
      resetChat();
      resetPlanExecutor();
      hasLoadedRef.current = null;
      setResumeConversationID(null);
    };
    window.addEventListener("backoffice-resume-chat", handleResumeChat);
    window.addEventListener("backoffice-new-chat", handleNewChat);
    return () => {
      window.removeEventListener("backoffice-resume-chat", handleResumeChat);
      window.removeEventListener("backoffice-new-chat", handleNewChat);
    };
  }, [resetChat, resetPlanExecutor]);

  const reset = useCallback(() => {
    hasLoadedRef.current = null;
    setResumeConversationID(null);
  }, []);

  return { isLoadingResume, reset };
};
